import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Loader2, MapPin, Plus, Save, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  useFetchListProvinces,
  useFetchServicePreferences,
  useUpdateServicePreferences,
} from '@/hooksApi';
import { SettingsBreadcrumb } from '@/components/SettingsBreadcrumb';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ROUTE } from '@/config';
import { toast } from '@/hooks/use-toast';
import { buildProvinceOptions } from '@/utils/provinceLookup';
import type { ServicePreferences, ServiceProvinceRule } from '@/types';
import ServiceIcon from '@/components/OrderForm/ServiceIcon';

/**
 * Layer ② of the service-level model, as a settings page.
 *
 * "All my orders go Priority, except Karbala on Economy" — the merchant's
 * standing rules for which service their orders ship on.
 *
 * NO PRICES HERE, by decision. The catalogue is a list of promises; the price
 * is quoted once, on the create-order screen, for the order being created. A
 * price list on this page would be a second source of truth that drifts from
 * what the merchant is actually charged.
 */

// RadioGroup cannot hold `null`, but "no rule of my own" is a real, distinct
// choice that has to survive to the wire as null. This sentinel only ever
// exists in the radio's value space — it is translated back to null on the way
// into state, so it can never reach the payload.
const PLATFORM_DEFAULT = '__platform_default__';

// A rule has no server id, and a blank new row has no province_code either, so
// nothing in the payload is unique enough to key a row on. Row identity is
// therefore minted client-side and stripped before the PUT. Without it React
// reuses the wrong Select instance when a middle row is deleted: the rows shift
// up while the open dropdown and its typed filter stay pinned to the element.
interface ProvinceRuleRow extends ServiceProvinceRule {
  id: string;
}

let rowSequence = 0;
const nextRowId = () => `service-rule-${++rowSequence}`;

const CARD = 'rounded-2xl border border-sd-border bg-sd-card shadow-sm';
// inline-flex, not flex: in the action bar it is a flex child either way, but
// in the empty state a block-level flex button would stretch to the full width
// of the card. DESIGN_SPEC's empty-state CTA uses inline-flex for that reason.
const PRIMARY_BUTTON =
  'inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity';
const RADIO_ITEM =
  'mt-0.5 shrink-0 border-sd-input text-sd-foreground focus-visible:ring-sd-ring ring-offset-sd-background';

const optionRowClass = (selected: boolean) =>
  `flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
    selected ? 'border-sd-primary bg-sd-muted/40' : 'border-sd-border hover:bg-sd-muted/30'
  }`;

// The non-error branch has to re-supply border/bg: cn() only drops base classes
// that conflict with what is passed, so omitting them leaves the trigger on
// SelectTrigger's own defaults and 2px off the inputs next to it.
const triggerClass = (hasError: boolean) =>
  `h-10 rounded-lg text-sm text-sd-foreground focus:ring-sd-ring ${
    hasError ? 'border-red-500 bg-red-50' : 'border-sd-input bg-sd-input-bg'
  }`;

interface PageShellProps {
  children: ReactNode;
}

const PageShell = ({ children }: PageShellProps) => (
  <div className='sd-scope sd-font flex flex-col'>
    <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
      <SettingsBreadcrumb
        moduleName='service_level'
        moduleRoute={ROUTE.SETTINGS_SERVICE_PREFERENCE}
      />
      {children}
    </div>
  </div>
);

/**
 * UI-F15 — "somebody else saved while this tab was open", or not.
 *
 * The backend answers a stale-version PUT with HTTP 409 and puts the CURRENT
 * rule set in the body, so this returns that state (to re-seed the form) or
 * null for every other failure.
 *
 * Typed here rather than on `ErrorMessageResponse`, which models only
 * `response.data`. The runtime value is an AxiosError and does carry
 * `response.status`; widening the shared error type for one call site would
 * make every other consumer's optional-chaining look unnecessary.
 */
const staleVersionConflict = (error: unknown): ServicePreferences | null => {
  const response = (
    error as { response?: { status?: number; data?: { object?: ServicePreferences } } }
  )?.response;

  return response?.status === 409 ? (response.data?.object ?? null) : null;
};

const ServicePreferenceSection = () => {
  const { t, i18n } = useTranslation();
  // Same derivation the shipping-rules page uses — one convention, not two.
  const locale: 'en' | 'ar' = i18n.language?.startsWith('ar') ? 'ar' : 'en';
  const direction = locale === 'ar' ? 'rtl' : 'ltr';

  const { data, isLoading } = useFetchServicePreferences();
  const { data: provincesData } = useFetchListProvinces();
  const { mutate: save, isLoading: isSaving } = useUpdateServicePreferences();

  const [defaultServiceCode, setDefaultServiceCode] = useState<string | null>(null);
  const [provinceRules, setProvinceRules] = useState<ProvinceRuleRow[]>([]);
  // Has the merchant edited since the last time server state was applied?
  // React Query refetches on window focus, so without this the effect below
  // would copy server state over half-finished edits every time the merchant
  // tabbed away and back — silently discarding their work with no warning.
  const [isDirty, setIsDirty] = useState(false);
  // The exact rows a refused save objected to — NOT a global "show errors" flag.
  // A flag marked every incomplete row, so adding a fresh blank row either lit
  // it up red for work not yet started, or (if cleared on add) wiped the marks
  // off rows that were still genuinely in conflict, leaving the screen looking
  // resolved when nothing had been. Membership is captured at refusal; whether
  // a mark still SHOWS is re-derived from the row's current values below, which
  // is what makes it clear the moment the merchant fixes it.
  const [invalidRowIds, setInvalidRowIds] = useState<Set<string>>(new Set());

  const applyServerState = (preferences: ServicePreferences) => {
    setDefaultServiceCode(preferences.default_service_code);
    setProvinceRules(
      // Sorted on arrival only. The server returns the set unordered, so
      // without this the merchant's rules reshuffle on every reload. Sorting
      // here rather than on every render means a row the merchant just added
      // stays where they added it until the next load.
      [...preferences.provinces]
        .sort((a, b) => a.province_code.localeCompare(b.province_code))
        .map((rule) => ({ ...rule, id: nextRowId() })),
    );
    setInvalidRowIds(new Set());
  };

  // Seed local state once the server state lands. A save applies its own echo
  // directly (see handleSave) rather than relying on this effect re-running.
  useEffect(() => {
    if (!data || isDirty) return;
    applyServerState(data);
    // isDirty is deliberately not a dependency: this must not re-run and
    // clobber the form the moment the merchant reverts their last edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // The regions feed carries the 18 real Iraqi governorates plus thousands of
  // seeded rows whose province_code is an invented city name, repeated once per
  // area. Mapping it raw put 5,139 items into a non-virtualised Select and
  // froze the tab. buildProvinceOptions is the shared helper the shipping-rules
  // page already uses — it filters, dedupes, localises and sorts.
  //
  // The codes already on saved rules are passed through as a keep-list: the
  // filter is a heuristic, and a province the merchant has ALREADY chosen must
  // never disappear from the control that shows it, whatever its code looks
  // like. Order creation uses the same province_code as its picker value, so
  // anything selectable there stays selectable here once saved.
  const savedProvinceCodes = useMemo(
    () => provinceRules.map((rule) => rule.province_code).filter(Boolean),
    [provinceRules],
  );

  const provinceOptions = useMemo(
    () => buildProvinceOptions(provincesData?.object ?? [], locale, savedProvinceCodes),
    [provincesData, locale, savedProvinceCodes],
  );

  const duplicateProvinces = useMemo(() => {
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    for (const rule of provinceRules) {
      if (!rule.province_code) continue;
      if (seen.has(rule.province_code)) duplicates.add(rule.province_code);
      seen.add(rule.province_code);
    }
    return duplicates;
  }, [provinceRules]);

  // Derived, never stored. A stored message would survive the merchant fixing
  // the row it was complaining about; this one cannot, because it is recomputed
  // from the rows a refusal flagged AND their current values.
  const refusalMessage = useMemo(() => {
    const flagged = provinceRules.filter((rule) => invalidRowIds.has(rule.id));
    if (flagged.some((rule) => !rule.province_code || !rule.service_code)) {
      return String(t('service_preference_incomplete_rule'));
    }
    if (flagged.some((rule) => duplicateProvinces.has(rule.province_code))) {
      return String(t('service_preference_duplicate_province'));
    }
    return null;
  }, [provinceRules, invalidRowIds, duplicateProvinces, t]);

  const effectiveService = data?.services.find(
    (service) => service.code === data?.effective_default_service_code,
  );

  // A code that has left the catalogue still comes back on the saved
  // preference. Radix renders nothing for a value with no matching item, so
  // without this the field would go blank — no code, no placeholder, no mark —
  // while Save quietly shipped the dead code back up.
  const knownServiceCodes = useMemo(
    () => new Set((data?.services ?? []).map((service) => service.code)),
    [data],
  );
  const isUnknownService = (code: string | null) => Boolean(code) && !knownServiceCodes.has(code!);

  const addProvinceRule = () => {
    // A blank row the merchant fills in — both selects are required before Save
    // will send it, which is checked below rather than by disabling Add. The
    // new row carries a fresh id that is not in invalidRowIds, so it opens
    // clean without disturbing the marks on rows that are still in conflict.
    setIsDirty(true);
    setProvinceRules((rules) => [
      ...rules,
      { id: nextRowId(), province_code: '', service_code: '' },
    ]);
  };

  const updateProvinceRule = (id: string, patch: Partial<ServiceProvinceRule>) => {
    setIsDirty(true);
    setProvinceRules((rules) =>
      rules.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule)),
    );
  };

  const removeProvinceRule = (id: string) => {
    setIsDirty(true);
    setProvinceRules((rules) => rules.filter((rule) => rule.id !== id));
  };

  const selectDefaultService = (value: string) => {
    setIsDirty(true);
    // Cleared = "no rule of my own" — orders fall back to the platform default,
    // which is what effective_default_service_code reports.
    setDefaultServiceCode(value === PLATFORM_DEFAULT ? null : value);
  };

  const discardChanges = () => {
    if (data) applyServerState(data);
    setIsDirty(false);
  };

  const handleSave = () => {
    const complete = provinceRules.filter((rule) => rule.province_code && rule.service_code);

    // Refusals are reported INLINE, next to Save, not as a toast. The toast
    // viewport is anchored bottom-end and physically covered this action bar:
    // elementFromPoint over the Save button returned the toast, so a merchant
    // could not re-click Save until it expired. Inline also puts the reason in
    // the same glance as the button that was refused.
    //
    // Compared by length rather than just sending `complete`: a half-filled row
    // has to block the save, not be silently dropped out of it.
    if (complete.length !== provinceRules.length) {
      setInvalidRowIds(
        new Set(
          provinceRules
            .filter((rule) => !rule.province_code || !rule.service_code)
            .map((rule) => rule.id),
        ),
      );
      return;
    }

    // A province can only resolve to one service — the backend's unique key
    // would collapse duplicates silently, so refuse them here where the
    // merchant can still see which row to fix.
    if (duplicateProvinces.size > 0) {
      setInvalidRowIds(
        new Set(
          provinceRules
            .filter((rule) => duplicateProvinces.has(rule.province_code))
            .map((rule) => rule.id),
        ),
      );
      return;
    }

    save(
      {
        default_service_code: defaultServiceCode,
        // `id` is client-only row identity and never goes on the wire.
        provinces: complete.map(({ province_code, service_code }) => ({
          province_code,
          service_code,
        })),
        // UI-F15 — the version from the GET this edit was built on. The
        // backend guard shipped and works, but a version-LESS save is a
        // deliberate force-overwrite and the dashboard sent none — so the
        // original defect (two Settings tabs, the later save wipes the
        // earlier one's rules with a 200 and no signal) still reproduced.
        //
        // `undefined` when the backend predates the field, which keeps the
        // old force-overwrite behaviour rather than breaking the save.
        version: data?.version,
      },
      {
        onSuccess: (result) => {
          // The PUT echoes what actually persisted, so apply it directly.
          // Going through the seeding effect instead would depend on the cache
          // write and the isDirty clear landing in the same render pass — when
          // they don't, the echo is dropped and, with Save now disabled while
          // clean, the merchant has no way to re-push.
          setIsDirty(false);
          applyServerState(result);
          toast({ title: String(t('service_preferences_saved')) });
        },
        onError: (error) => {
          // UI-F15 — 409 is NOT a generic failure. Somebody else saved while
          // this tab was open, so the merchant's edits are still valid work
          // built on a stale base; the answer is "here is what it looks like
          // now, redo your change", not a red toast they cannot act on.
          //
          // The 409 body carries the CURRENT state and its new version, so
          // seeding from it re-arms the form without a reload — and isDirty
          // is cleared because the on-screen rules are now the server's, not
          // the merchant's unsaved ones.
          const conflict = staleVersionConflict(error);

          if (conflict) {
            setIsDirty(false);
            applyServerState(conflict);

            toast({
              title: String(t('service_preferences_changed_elsewhere')),
              variant: 'destructive',
            });
          }
          // Anything else: no toast here. useSentryMutation already surfaces
          // it, and isDirty deliberately stays set so a retry keeps the edits.
        },
      },
    );
  };

  if (isLoading) {
    return (
      <PageShell>
        <div className='space-y-3'>
          {[0, 1].map((block) => (
            <div key={block} className={`${CARD} p-8`}>
              <div className='animate-pulse space-y-4'>
                <div className='h-4 bg-sd-muted rounded w-3/4' />
                <div className='h-4 bg-sd-muted rounded w-1/2' />
                <div className='h-4 bg-sd-muted rounded w-5/6' />
              </div>
            </div>
          ))}
        </div>
      </PageShell>
    );
  }

  // Same guard the create-order selector applies: with fewer than two live
  // services there is nothing to choose between, so the editor renders
  // nothing rather than offering a one-option menu. This is what keeps the
  // feature inert on deploy day — the sidebar entry is hidden on the same
  // condition, and this covers anyone who reaches the URL directly.
  if (!data || data.services.length < 2) {
    return null;
  }

  return (
    <PageShell>
      <div>
        <h1 className='text-xl font-semibold text-sd-foreground m-0'>{t('service_level')}</h1>
        <p className='text-sm text-sd-muted-foreground mt-1'>{t('service_level_description')}</p>
      </div>

      {/* CARD A — the account-wide default */}
      <section className={CARD}>
        <div className='px-5 py-4 border-b border-sd-border'>
          <h3 className='text-base font-semibold'>{t('default_service')}</h3>
          <p className='text-xs text-sd-muted-foreground mt-0.5'>{t('default_service_hint')}</p>
        </div>
        <div className='p-5 space-y-4'>
          <RadioGroup
            value={defaultServiceCode ?? PLATFORM_DEFAULT}
            onValueChange={selectDefaultService}
            className='gap-3'
            // Radix defaults this to 'ltr'. Left unset, the radio circles stayed
            // pinned left in Arabic while every sibling on the page mirrored —
            // and Arabic is this app's default locale.
            dir={direction}
          >
            {/* SVC-STEP — the "no standing default" row, reworded.

                It used to be named after the platform's answer ("Boxy default —
                Boxy Select"), which described a MECHANISM the merchant does not
                control and cannot see. What they actually get by leaving this
                unset is the choice itself, on the order screen, every time — so
                that is what it now says.

                ⚠️ COPY ONLY. This is still the same two states it always was:
                a service code, or nothing. `null` on the wire is unchanged, and
                every reader of it — ServicePreferenceResolver, the merchant
                UpdateRequest, the trusted pricing view — still treats absence
                as "fall through to the platform default". No third state was
                introduced, which is exactly why no reader had to learn one. */}
            <label className={optionRowClass(defaultServiceCode === null)}>
              <RadioGroupItem value={PLATFORM_DEFAULT} className={RADIO_ITEM} />
              <span className='space-y-1'>
                <span className='block text-sm font-medium text-sd-foreground'>
                  {String(t('service_default_choose_per_order'))}
                </span>
                {/* effective_default_service_code is what an order created RIGHT
                    NOW would resolve to — which INCLUDES the merchant's own
                    saved choice. It only describes the platform's answer while
                    the merchant has chosen nothing, so that is the only case
                    where it is named. Here it carries real weight: it is the
                    answer to "what happens if I skip the step?". */}
                <span className='block text-xs text-sd-muted-foreground'>
                  {defaultServiceCode === null && effectiveService
                    ? String(
                        t('service_default_choose_per_order_hint', {
                          service: effectiveService.title,
                        }),
                      )
                    : String(t('service_default_choose_per_order_hint_generic'))}
                </span>
              </span>
            </label>

            {/* The saved default points at a code the catalogue no longer
                carries: without a row to check, every radio reads unselected
                and the merchant cannot see what their account is set to. */}
            {isUnknownService(defaultServiceCode) ? (
              <label className={optionRowClass(true)}>
                <RadioGroupItem
                  value={defaultServiceCode as string}
                  className={RADIO_ITEM}
                  disabled
                />
                <span className='text-sm font-medium text-sd-foreground'>{defaultServiceCode}</span>
              </label>
            ) : null}

            {data.services.map((service) => (
              <label
                key={service.code}
                className={optionRowClass(defaultServiceCode === service.code)}
              >
                <RadioGroupItem value={service.code} className={RADIO_ITEM} />
                <span className='space-y-1'>
                  <span className='flex flex-wrap items-center gap-2'>
                    {/* Same mark as the create-order step and order details.
                        ServiceIcon renders nothing for a service with no
                        glyph, so the row simply reads as text. */}
                    <ServiceIcon code={service.code} className='h-4 w-4 text-sd-foreground' />
                    <span className='text-sm font-medium text-sd-foreground'>{service.title}</span>
                    {/* delivery_speed and description are the whole point of the
                        redesign: the API has always returned them and the
                        merchant was being asked to choose between bare names. */}
                    {service.delivery_speed ? (
                      <span className='inline-flex items-center rounded-full border border-sd-border bg-sd-muted px-2 py-0.5 text-xs font-medium text-sd-muted-foreground'>
                        {service.delivery_speed}
                      </span>
                    ) : null}
                  </span>
                  {service.description ? (
                    <span className='block text-xs text-sd-muted-foreground'>
                      {service.description}
                    </span>
                  ) : null}
                </span>
              </label>
            ))}
          </RadioGroup>
        </div>
      </section>

      {/* CARD B — per-province overrides */}
      <section className={CARD}>
        <div className='px-5 py-4 border-b border-sd-border flex items-center justify-between gap-3'>
          <div>
            <h3 className='text-base font-semibold'>{t('province_overrides')}</h3>
            <p className='text-xs text-sd-muted-foreground mt-0.5'>
              {t('province_overrides_hint')}
            </p>
          </div>
          {/* Card-header action, sized like the shipping-rules one it sits
              beside in the settings nav — not the page-level primary. */}
          <Button
            onClick={addProvinceRule}
            className='h-9 gap-1.5 text-xs font-semibold bg-sd-primary text-sd-primary-foreground hover:bg-sd-primary/90'
          >
            <Plus className='h-4 w-4' />
            {t('add_province_rule')}
          </Button>
        </div>

        <div className='p-5 space-y-4'>
          {provinceRules.length === 0 ? (
            <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-8 text-center'>
              <MapPin className='h-10 w-10 mx-auto text-sd-muted-foreground mb-3' />
              <p className='text-sm text-sd-muted-foreground'>{t('no_province_rules')}</p>
              <p className='text-xs text-sd-muted-foreground mt-1 mb-4'>
                {t('no_province_rules_hint')}
              </p>
              <button
                type='button'
                onClick={addProvinceRule}
                className={`${PRIMARY_BUTTON} mx-auto`}
              >
                <Plus className='h-4 w-4' />
                {t('add_province_rule')}
              </button>
            </div>
          ) : (
            <div className='space-y-3'>
              {provinceRules.map((rule) => {
                const flagged = invalidRowIds.has(rule.id);
                const provinceError =
                  flagged && (!rule.province_code || duplicateProvinces.has(rule.province_code));
                // A retired code is wrong whether or not a save has been tried,
                // so it marks the field immediately rather than waiting for one.
                const staleService = isUnknownService(rule.service_code);
                const serviceError = (flagged && !rule.service_code) || staleService;

                return (
                  <div key={rule.id} className='rounded-xl border border-sd-border p-3'>
                    {/* items-start, not items-end: an error message under one
                        field made its sibling's label jump 20px while the other
                        stayed put. Top-aligned, the row is stable whether or not
                        a message is showing. */}
                    <div className='flex flex-col sm:flex-row sm:items-start gap-3'>
                      <div className='flex-1 min-w-0'>
                        <label className='block text-xs font-medium text-sd-muted-foreground mb-1.5'>
                          {t('province')}
                        </label>
                        <Select
                          value={rule.province_code}
                          onValueChange={(value) =>
                            updateProvinceRule(rule.id, { province_code: value })
                          }
                          dir={direction}
                        >
                          <SelectTrigger className={triggerClass(provinceError)}>
                            {/* Placeholder deliberately differs from the label
                                above it — "Province" over a box reading
                                "Province" made an empty row look filled in. */}
                            <SelectValue placeholder={String(t('select_province'))} />
                          </SelectTrigger>
                          <SelectContent>
                            {provinceOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {/* Colour alone is not a message: without this the two
                            rows of a duplicate-province clash both go red with
                            nothing saying why. */}
                        {provinceError ? (
                          <p className='text-xs text-red-500 mt-1' role='alert'>
                            {duplicateProvinces.has(rule.province_code)
                              ? t('service_preference_duplicate_province')
                              : t('this_field_is_required')}
                          </p>
                        ) : null}
                      </div>

                      <div className='flex-1 min-w-0'>
                        <label className='block text-xs font-medium text-sd-muted-foreground mb-1.5'>
                          {t('select_service')}
                        </label>
                        <Select
                          value={rule.service_code}
                          onValueChange={(value) =>
                            updateProvinceRule(rule.id, { service_code: value })
                          }
                          dir={direction}
                        >
                          <SelectTrigger className={triggerClass(serviceError)}>
                            {/* Radix renders nothing at all for a value with no
                                matching item — and suppresses the placeholder
                                too — so a retired code would leave the field
                                blank. Show the raw code instead. */}
                            {staleService ? (
                              <span className='truncate'>{rule.service_code}</span>
                            ) : (
                              <SelectValue placeholder={String(t('select_service'))} />
                            )}
                          </SelectTrigger>
                          <SelectContent>
                            {data.services.map((service) => (
                              <SelectItem key={service.code} value={service.code}>
                                <span className='flex items-center gap-2'>
                                  <ServiceIcon
                                    code={service.code}
                                    className='h-4 w-4 text-sd-foreground'
                                  />
                                  {service.title}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {serviceError ? (
                          <p className='text-xs text-red-500 mt-1' role='alert'>
                            {staleService ? t('select_service') : t('this_field_is_required')}
                          </p>
                        ) : null}
                      </div>

                      {/* An invisible stand-in for the field labels, so the
                          button lines up with the SELECTS rather than with the
                          labels above them — without hardcoding a pixel offset
                          that a font change would silently break. */}
                      <div className='flex flex-col shrink-0'>
                        <span
                          aria-hidden='true'
                          className='hidden sm:block text-xs font-medium mb-1.5 select-none invisible'
                        >
                          &nbsp;
                        </span>
                        <div className='flex h-10 items-center'>
                          {/* after:-inset-1.5 is a transparent 44x44 hit area over
                              a 32px button — the design system's icon-button size,
                              kept, without failing the mobile touch-target rule. */}
                          <button
                            type='button'
                            aria-label={String(t('delete'))}
                            onClick={() => removeProvinceRule(rule.id)}
                            className='relative h-8 w-8 rounded-lg border border-sd-input bg-sd-background flex items-center justify-center text-sd-muted-foreground hover:bg-sd-destructive/10 hover:text-sd-destructive transition-colors after:absolute after:-inset-1.5 after:content-[""]'
                          >
                            <Trash2 className='h-4 w-4' />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <div className='flex flex-wrap items-center justify-end gap-3'>
        {/* The refusal reason, inline, in the same glance as the button that was
            refused — see handleSave for why this is not a toast. It is derived,
            not stored, so correcting the last offending row clears it. */}
        {refusalMessage ? (
          <p className='me-auto text-xs text-red-500' role='alert'>
            {refusalMessage}
          </p>
        ) : null}
        {isDirty ? (
          <>
            <span className='text-xs text-sd-muted-foreground'>{t('unsaved_changes')}</span>
            <button
              type='button'
              onClick={discardChanges}
              className='px-4 py-2.5 rounded-lg border border-sd-input bg-sd-background text-sd-foreground text-sm font-semibold hover:bg-sd-muted transition-colors'
            >
              {t('discard')}
            </button>
          </>
        ) : null}
        <button
          type='button'
          onClick={handleSave}
          disabled={!isDirty || isSaving}
          className={`${PRIMARY_BUTTON} disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {/* The icon slot is always occupied so the button does not jump 24px
              wider the instant it is clicked. */}
          {isSaving ? <Loader2 className='h-4 w-4 animate-spin' /> : <Save className='h-4 w-4' />}
          {isSaving ? t('saving') : t('save')}
        </button>
      </div>
    </PageShell>
  );
};

export default ServicePreferenceSection;
